import {RefObject, useState} from 'react';

import {PaymentHandlers, PrimerHeadlessCheckout} from '@primer-io/checkout-web';
import {AxiosError} from 'axios';
import {useDispatch} from 'react-redux';

import {Dispatch} from 'redux';

import {getDefaultErrorMessage} from 'libs/retryError/retryError';

import {isUhcEnabled} from 'libs/rollout';

import {PAYMENTS} from '../../constants/paymentStep';

import {completeOrderReviewPending} from '../../store/actions/completeOrder/completeOrder';

import {setAnalyticsMessage, setUiMessage} from '../../store/reducers/primer/primer';

import {useValidateOrder} from './useValidateOrder/useValidateOrder';

type PaymentCreationHandlersType = {
    onBeforePaymentCreate: Required<PaymentHandlers>['onBeforePaymentCreate'];
    onPaymentCreationStart: Required<PaymentHandlers>['onPaymentCreationStart'];
    isValidating: boolean;
};
type ErrorType = AxiosError<{diagnostic_code?: string; messages: {error: {message: string}[]}}>;

const getAnalyticData = (error: ErrorType) => {
    return {
        code: error.response?.data?.diagnostic_code || error.code || '',
        status: error.response?.status || 'Unknown',
    };
};

const getErrorMessage = (error: ErrorType) => error.response?.data?.messages?.error?.[0]?.message;
const handleError = (dispatch: Dispatch, error: ErrorType) => {
    const errorMessage = getErrorMessage(error);
    const analyticData = getAnalyticData(error);

    dispatch(
        setAnalyticsMessage(
            `prevalidate - ${isUhcEnabled() ? '(UHC) ' : ''}${errorMessage || ''} - code - ${analyticData.code} - response status - ${analyticData.status}`,
        ),
    );
    dispatch(setUiMessage(errorMessage || getDefaultErrorMessage(error)));
};

/**
 * Handle primer payment creation phases
 * before payment creation validates order to ensure that quote is valid and didn't change after create session
 * after payment creation shows Placing Order loading screen
 */
export const usePaymentCreationHandlers = (
    paymentMethod: PAYMENTS,
    primerRef: RefObject<PrimerHeadlessCheckout | null>,
): PaymentCreationHandlersType => {
    const dispatch = useDispatch();
    const {validateOrder} = useValidateOrder();
    const [isValidating, setIsValidating] = useState(false);

    return {
        isValidating,
        onBeforePaymentCreate: async (_, handler) => {
            try {
                setIsValidating(true);
                await validateOrder(paymentMethod);
                if (paymentMethod === PAYMENTS.METHOD_PRIMER_KLARNA) {
                    await primerRef.current?.refreshClientSession();
                }
                handler.continuePaymentCreation();
            } catch (error) {
                handleError(dispatch, error);
                handler.abortPaymentCreation();
            } finally {
                setIsValidating(false);
            }
        },
        onPaymentCreationStart: () => {
            dispatch(completeOrderReviewPending());
        },
    };
};
